import { Box, LinearProgress, Typography, useTheme } from '@mui/material';
import { IoCheckmark } from 'react-icons/io5';
import { useFormContext } from 'react-hook-form';
import { getStepProgress, STEPS, stepHasError } from './utils';
import { useGetPublicAvailableIntegrations } from '@/apis/agency/businessOnboarding/hooks';

const Stepper = ({ activeStep, setActiveStep, isPublicRoute, isMobile, overallProgress, stepProgresses }: any) => {
  const theme = useTheme();
  const {
    watch,
    formState: { errors }
  } = useFormContext();

  const values = watch();
  const steps = STEPS(isPublicRoute);
  const selectedIndustry = watch('industry');

  const { data: availableIntegrations } = useGetPublicAvailableIntegrations(
    {
      per_page: 999,
      filter: { industries_contain: selectedIndustry }
    },
    { enabled: !!selectedIndustry }
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const integrationsList = availableIntegrations?.data || [];

  if (isMobile) {
    return (
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: '#fff',
          mx: -2,
          px: 2,
          py: 1.5,
          borderBottom: '1px solid #eee',
          boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
        }}
      >
        <Box display='flex' alignItems='center' gap={1.5} mb={2}>
          <LinearProgress
            variant='determinate'
            value={overallProgress}
            sx={{
              flex: 1,
              height: 6,
              borderRadius: 4,
              backgroundColor: '#E5E7EB',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                backgroundColor: overallProgress >= 100 ? '#22C55E' : 'primary.main'
              }
            }}
          />
          <Typography fontSize={12} fontWeight={600} color='#6B7280'>
            {overallProgress}%
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            '::-webkit-scrollbar': { display: 'none' },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none'
          }}
        >
          {steps.map((_, index) => {
            const isActive = index === activeStep;
            const stepProgress = stepProgresses?.[index] || 0;
            const isStepCompleted = stepProgress >= 100;
            const isUnCompleted = index < activeStep && stepProgress < 100;
            const hasError = stepHasError(index, values, errors, isPublicRoute);

            const stepCircleBorderColor = hasError
              ? theme.palette.error.main
              : isUnCompleted
                ? theme.palette.primary.main
                : isStepCompleted || isActive
                  ? 'transparent'
                  : '#E5E7EB';

            return (
              <Box
                key={index}
                display='flex'
                flexDirection='column'
                alignItems='center'
                onClick={() => setActiveStep(index)}
                sx={{ cursor: 'pointer', minWidth: 40 }}
              >
                <Box
                  width={32}
                  height={32}
                  borderRadius='50%'
                  display='flex'
                  alignItems='center'
                  justifyContent='center'
                  fontSize={14}
                  fontWeight={500}
                  sx={{
                    bgcolor: hasError ? '#FEF2F2' : isActive ? 'primary.main' : isStepCompleted ? '#22C55E' : '#fff',
                    border: `1px solid ${stepCircleBorderColor}`,
                    color: hasError ? theme.palette.error.main : isStepCompleted || isActive ? '#fff' : '#000',
                    transition: 'all 0.2s'
                  }}
                >
                  {isStepCompleted && !isActive && !hasError ? <IoCheckmark /> : index + 1}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  return (
    <Box position='relative'>
      {steps.map((step, index) => {
        const isActive = index === activeStep;
        const { progress } = getStepProgress(index, values, isPublicRoute, integrationsList);
        const hasError = stepHasError(index, values, errors, isPublicRoute);
        const isCompleted = progress >= 100;
        const isUnCompleted = index < activeStep && progress < 100;

        const stepCircleBorderColor = hasError
          ? theme.palette.error.main
          : isUnCompleted
            ? theme.palette.primary.main
            : '#E5E7EB';

        return (
          <Box key={index} display='flex' gap={2} alignItems='flex-start'>
            {/* LEFT STEP CIRCLE */}
            <Box display='flex' flexDirection='column' alignItems='center'>
              <Box
                width={32}
                height={32}
                borderRadius='50%'
                display='flex'
                alignItems='center'
                justifyContent='center'
                fontSize={14}
                fontWeight={500}
                onClick={() => setActiveStep(index)}
                sx={{
                  cursor: 'pointer',
                  bgcolor: hasError ? '#FEF2F2' : isActive ? 'primary.main' : isCompleted ? '#22C55E' : '#fff',
                  border: `1px solid ${stepCircleBorderColor}`,
                  color: hasError ? theme.palette.error.main : isCompleted || isActive ? '#fff' : '#000'
                }}
              >
                {isCompleted && !isActive && !hasError ? <IoCheckmark /> : index + 1}
              </Box>

              {index !== steps.length - 1 && (
                <Box
                  width='2px'
                  height='40px'
                  sx={{
                    bgcolor: isCompleted ? '#22C55E' : '#E5E7EB'
                  }}
                />
              )}
            </Box>

            <Box onClick={() => setActiveStep(index)} sx={{ cursor: 'pointer', flex: 1 }}>
              <Typography
                fontSize={14}
                fontWeight={hasError ? 600 : 400}
                color={hasError ? theme.palette.error.main : '#000'}
              >
                {step.label}
              </Typography>

              <Typography fontSize={12} color='#777777' mt={-0.5} noWrap>
                {step.helperText}
              </Typography>

              <Box display='flex' alignItems='center' gap={1}>
                <LinearProgress
                  variant='determinate'
                  value={progress}
                  sx={{
                    flex: 1,
                    height: 6,
                    borderRadius: 4,
                    backgroundColor: '#E5E7EB',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      backgroundColor: progress >= 100 ? '#22C55E' : 'primary.main'
                    }
                  }}
                />
                <Typography fontSize={12} color='#6B7280'>
                  {progress}%
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default Stepper;
